// src/components/CustomerUserManagement.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardHeader,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { Edit, Block } from "@mui/icons-material";
import EnvironmentCard from "./common/EnvironmentCard";
import {
  getCompanies,
  getCompanyEnvironments,
  getUserCompany,
  getEnvironmentUsers,
  addEnvironmentUser,
  updateEnvironmentUser,
  toggleEnvironmentUser,
  getCompanyEnvironmentDetails,
  updateEnvironmentUserLimit,
  getAppUserUsage,
  updateCompanyApp
} from "../services/api";
import { SubTitle, Title } from "./common/Title";
import { getUserRole } from "../services/getUserRole";

const CustomerUserManagement = ({ token }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [environmentUsers, setEnvironmentUsers] = useState([]);
  const [environmentDetails, setEnvironmentDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "employee",
    nome_completo: "",
    email: "",
    user_code: "",
    projects: [],
  });
  const [editingUser, setEditingUser] = useState(null);
  const [newProjectsInputValue, setNewProjectsInputValue] = useState("");
  const [editProjectsInputValue, setEditProjectsInputValue] = useState("");
  const [editingProjects, setEditingProjects] = useState([]);
  const [updatePassword, setUpdatePassword] = useState("");
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("");
  const [editingEnvironment, setEditingEnvironment] = useState(null);
  const [newMaxUsers, setNewMaxUsers] = useState("");
  const [appUsageMap, setAppUsageMap] = useState({});

  // Determine user role on mount; fetch either all companies or just the user’s assigned company
  useEffect(() => {
    const role = getUserRole(); // e.g. "customer" or "admin"
    setUserRole(role);

    if (role === "customer") {
      setUserCompany();
    } else {
      fetchCompanies();
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchEnvironmentDetails(selectedCompany);
      fetchAppUsageData(selectedCompany);
    }
  }, [selectedCompany]);

  const fetchAppUsageData = async (companyId) => {
    try {
      const data = await getAppUserUsage(token, companyId);
      const usageMap = {};
      data.forEach((entry) => {
        usageMap[entry.app_id] = {
          company_app_id:     entry.company_app_id,
          max_users: entry.max_users,
          total_unique_users: entry.total_unique_users,
          company_id: entry.company_id,
        app_id: entry.app_id,
        app_name: entry.app_name,
        };
      });
      setAppUsageMap(usageMap);
    } catch (err) {
      console.log(err);
      setError("Erro ao buscar uso de usuários por app.");
    }
  };

  // Fetch the single company assigned to this "customer" user
  async function setUserCompany() {
    try {
      const data = await getUserCompany(token);
      // Example response: { company_id: 9, company_name: "Acme Corp" }
      console.log("User’s assigned company:", data.company_id);

      // Set state
      setSelectedCompany(data.company_id);
      // We rely on the effect below to fetch environments
    } catch (err) {
      console.error("Error fetching user company:", err);
      setError("Erro ao buscar a empresa do usuário.");
    }
  }

  // Whenever selectedCompany changes (whether admin or customer), fetch environments
  useEffect(() => {
    if (selectedCompany) {
      fetchEnvironments(selectedCompany);
    }
  }, [selectedCompany]);

  // Whenever selectedEnvironment changes, fetch environment users
  useEffect(() => {
    if (selectedEnvironment) {
      fetchEnvironmentUsers(selectedEnvironment);
    }
  }, [selectedEnvironment]);

  // Admin/manager fetch all companies
  async function fetchCompanies() {
    try {
      const data = await getCompanies(token);
      setCompanies(data);
      if (data.length > 0) {
        setSelectedCompany(data[0].id);
      }
    } catch {
      setError("Erro ao buscar empresas.");
    }
  }

  async function fetchEnvironments(companyId) {
    try {
      const data = await getCompanyEnvironments(token, companyId);
      setEnvironments(data);
      if (data.length > 0) {
        setSelectedEnvironment(data[0].AliasAccess);
      } else {
        setSelectedEnvironment("");
      }
    } catch {
      setError("Erro ao buscar ambientes da empresa.");
    }
  }

  async function fetchEnvironmentUsers(environmentId) {
    try {
      const data = await getEnvironmentUsers(token, environmentId);
      setEnvironmentUsers(data);
    } catch {
      setError("Erro ao buscar usuários do ambiente.");
    }
  }

  // ------------------ CRUD Handlers ------------------
  const handleUpdateEnvironmentLimit = async () => {
    try {
      await updateCompanyApp(
        token,
        editingEnvironment.company_app_id,   // o ID da linha em company_apps
        { max_users: newMaxUsers }          // payload
      );
      fetchAppUsageData(selectedCompany);
      setEditingEnvironment(null);
    } catch (err) {
      console.error(err);
      setError("Erro ao atualizar o limite de usuários.");
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password || !selectedEnvironment) {
      setError("Nome de usuário, senha e ambiente são obrigatórios.");
      return;
    }
    try {
      await addEnvironmentUser(token, {
        ...newUser,
        environmentId: selectedEnvironment,
        projects: newUser.projects,
      });
      fetchEnvironmentUsers(selectedEnvironment);
      setNewUser({
        username: "",
        password: "",
        role: "employee",
        nome_completo: "",
        email: "",
        user_code: "",
        projects: [],
      });
    } catch {
      setError("Erro ao adicionar usuário.");
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      const updatedData = {
        username: editingUser.username,
        role: editingUser.role,
        is_enabled: editingUser.is_enabled,
        nome_completo: editingUser.nome_completo,
        email: editingUser.email,
        user_code: editingUser.user_code,
        projects: editingProjects,
      };
      if (updatePassword.trim() !== "") {
        updatedData.password = updatePassword;
      }
      await updateEnvironmentUser(token, editingUser.id, updatedData);
      setEditingUser(null);
      setUpdatePassword("");
      fetchEnvironmentUsers(selectedEnvironment);
    } catch {
      setError("Erro ao atualizar usuário.");
    }
  };

  const handleToggleUser = async (id) => {
    try {
      await toggleEnvironmentUser(token, id);
      fetchEnvironmentUsers(selectedEnvironment);
      // fetchEnvironmentDetails(selectedCompany);
      fetchAppUsageData(selectedCompany);
    } catch {
      setError("Erro ao alternar o status do usuário.");
    }
  };

  async function fetchEnvironmentDetails(companyId) {
    try {
      const data = await getCompanyEnvironmentDetails(token, companyId);
      setEnvironmentDetails(data);
    } catch (err) {
      setError("Erro ao buscar detalhes dos ambientes.");
    }
  }

  const traduzirCargo = (role) => {
    switch (role) {
      case "employee":
        return "Colaborador";
      case "manager":
        return "Gestor";
      default:
        return role;
    }
  };

  // Filter environments by search term (AliasAccess)
  const filteredEnvironments = environmentDetails.filter((env) =>
    env.AliasAccess.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ------------------ Render ------------------
  return (
    <Container sx={{ padding: 0, mt: 2 }}>
      <CardContent>
        <Grid container spacing={4}>
        {Object.values(appUsageMap).map((usage) => (
  <Grid2
    sx={{ position: "relative" }}
    item
    xs={6}
    sm={6}
    md={4}
    key={usage.app_id}
  >
    <EnvironmentCard usageData={usage} />
              {userRole === "admin" && (
                <IconButton
                  onClick={() => {
                    setEditingEnvironment(usage);
                    setNewMaxUsers(usage.max_users);
                  }}
                  sx={{
                    position: "absolute",
                    top: 1,
                    right: 1,
                  }}
                >
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Grid2>
          ))}
        </Grid>
      </CardContent>
      <Title>👥 Usuários do Cliente</Title>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Only show "Select Company" if user is NOT a customer */}
      {userRole !== "customer" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <SubTitle>🏢 Selecione a empresa</SubTitle>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* If environments are available, let user select an environment */}
      {environments.length > 0 && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <SubTitle>🌐 Selecione o ambiente</SubTitle>
            <FormControl fullWidth>
              <InputLabel>Ambiente</InputLabel>
              <Select
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
              >
                {environments.map((env) => (
                  <MenuItem key={env.AliasAccess} value={env.AliasAccess}>
                    {env.AliasAccess}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* If an environment is selected, show the users table and add-user form */}
      {selectedEnvironment && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent>
                <SubTitle>📋 Usuários do ambiente</SubTitle>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#1976d2" }}>
                      <TableRow>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Código
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Projetos
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Usuário
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Nome Completo
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          Cargo
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "white",
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {environmentUsers.length > 0 ? (
                        environmentUsers.map((user) => (
                          <TableRow
                            key={user.id}
                            sx={{
                              backgroundColor:
                                user.is_enabled === 1
                                  ? ""
                                  : "rgba(224, 32, 32, 0.1)",
                            }}
                          >
                            <TableCell>{user.user_code}</TableCell>
                            <TableCell>
                              {(user.projects || []).map((proj, idx) => (
                                <Chip
                                  key={idx}
                                  label={proj}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </TableCell>

                            <TableCell>{user.username}</TableCell>
                            <TableCell>{user.nome_completo}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{traduzirCargo(user.role)}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  setEditingUser(user);
                                  setEditingProjects(user.projects || []);
                                }}
                              >
                                <Edit sx={{ fontSize: 20 }} />
                              </IconButton>
                              <IconButton
                                color={user.is_enabled ? "error" : "success"}
                                onClick={() => handleToggleUser(user.id)}
                              >
                                <Block sx={{ fontSize: 20 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body1" color="textSecondary">
                              Nenhum usuário encontrado.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent>
                <SubTitle>➕ Adicionar usuário</SubTitle>
                <TextField
                  label="Nome de usuário"
                  fullWidth
                  margin="normal"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                />
                <TextField
                  label="Senha"
                  type="password"
                  fullWidth
                  margin="normal"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                />
                <TextField
                  label="Nome Completo"
                  fullWidth
                  margin="normal"
                  value={newUser.nome_completo}
                  onChange={(e) =>
                    setNewUser({ ...newUser, nome_completo: e.target.value })
                  }
                />
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                />
                <TextField
                  label="Código"
                  type="text"
                  fullWidth
                  margin="normal"
                  value={newUser.user_code}
                  onChange={(e) =>
                    setNewUser({ ...newUser, user_code: e.target.value })
                  }
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={newUser.projects}
                  inputValue={newProjectsInputValue}
                  onInputChange={(_, v) => setNewProjectsInputValue(v)}
                  onChange={(_, val) =>
                    setNewUser({ ...newUser, projects: val })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ";") {
                      e.preventDefault();
                      const tag = newProjectsInputValue.trim();
                      if (tag && !newUser.projects.includes(tag)) {
                        setNewUser((prev) => ({
                          ...prev,
                          projects: [...prev.projects, tag],
                        }));
                      }
                      setNewProjectsInputValue("");
                    }
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, idx) => (
                      <Chip
                        key={idx}
                        label={option}
                        {...getTagProps({ idx })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Projetos"
                      placeholder="Digite e tecle Enter ou ;"
                      value={newProjectsInputValue}
                      onChange={params.inputProps.onChange}
                    />
                  )}
                />
                <FormControl fullWidth sx={{ mb: 2, mt: 2, borderRadius: 3 }}>
                  <InputLabel id="cargo-label">Cargo</InputLabel>
                  <Select
                    labelId="cargo-label"
                    label="Cargo"
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                  >
                    <MenuItem value="employee">Colaborador</MenuItem>
                    <MenuItem value="manager">Gestor</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleAddUser}
                >
                  Adicionar usuário
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {editingUser && (
        <Dialog open onClose={() => setEditingUser(null)}>
          <DialogTitle>Editar usuário</DialogTitle>
          <DialogContent>
            <TextField
              label="Nome de usuário"
              fullWidth
              margin="dense"
              value={editingUser.username}
              onChange={(e) =>
                setEditingUser({ ...editingUser, username: e.target.value })
              }
            />
            <TextField
              label="Nova senha (opcional)"
              type="password"
              fullWidth
              margin="dense"
              value={updatePassword}
              onChange={(e) => setUpdatePassword(e.target.value)}
            />
            <TextField
              label="Nome Completo"
              fullWidth
              margin="dense"
              value={editingUser.nome_completo}
              onChange={(e) =>
                setEditingUser({
                  ...editingUser,
                  nome_completo: e.target.value,
                })
              }
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="dense"
              value={editingUser.email}
              onChange={(e) =>
                setEditingUser({ ...editingUser, email: e.target.value })
              }
            />
            <TextField
              label="Código"
              type="text"
              fullWidth
              margin="dense"
              value={editingUser.user_code}
              onChange={(e) =>
                setEditingUser({ ...editingUser, user_code: e.target.value })
              }
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={editingProjects}
              inputValue={editProjectsInputValue}
              onInputChange={(_, v) => setEditProjectsInputValue(v)}
              onChange={(_, val) => setEditingProjects(val)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ";") {
                  e.preventDefault();
                  const tag = editProjectsInputValue.trim();
                  if (tag && !editingProjects.includes(tag)) {
                    setEditingProjects((prev) => [...prev, tag]);
                  }
                  setEditProjectsInputValue("");
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, idx) => (
                  <Chip key={idx} label={option} {...getTagProps({ idx })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Projetos"
                  placeholder="Digite e tecle Enter"
                  margin="dense"
                  value={editProjectsInputValue}
                  onChange={params.inputProps.onChange}
                />
              )}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="cargo-label">Cargo</InputLabel>
              <Select
                labelId="cargo-label"
                label="Cargo"
                value={editingUser.role}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, role: e.target.value })
                }
              >
                <MenuItem value="employee">Colaborador</MenuItem>
                <MenuItem value="manager">Gestor</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateUser} color="primary">
              Salvar
            </Button>
            <Button onClick={() => setEditingUser(null)} color="secondary">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Environment Edit Dialog (for updating max_users) */}
      {editingEnvironment && (
        <Dialog open onClose={() => setEditingEnvironment(null)}>
          <DialogTitle>Editar Limite de Usuários</DialogTitle>
          <DialogContent>
            <TextField
              label="Máximo de Usuários"
              type="number"
              fullWidth
              margin="dense"
              value={newMaxUsers}
              onChange={(e) => setNewMaxUsers(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateEnvironmentLimit} color="primary">
              Salvar
            </Button>
            <Button
              onClick={() => setEditingEnvironment(null)}
              color="secondary"
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default CustomerUserManagement;
